import React from 'react'
import context from '@/context'
import { ValidationBoundary } from '@/components/validation'
import { ModalBody } from 'reactstrap'
import { BasicHeader } from '@/dialogs/common/basicHeader'
import { FormLoader, FormRow } from '@/components/changeRequests'
import { ChangeItemDialogFooter } from '@/dialogs/common/changeItemDialogFooter'
import { usePropertyReader } from '@/hooks/usePropertyReader'
import { DualLanguageRow } from '@/components/changeRequests/dualLanguageRow'
import { useCoupledProps } from '@/components/changeRequests/useCoupledProps'

const AddressDialog = React.memo(function AddressDialog (props) {
  return (
    <FormLoader {...props} render={() =>
      <ValidationBoundary>
        <BasicHeader title='Persons.Address' {...props} />
        <ModalBody>
          <AddressForm {...props} />
        </ModalBody>
        <ChangeItemDialogFooter {...props} />
      </ValidationBoundary>
    } />
  )
})

const AddressForm = React.memo(function AddressForm (props) {
  let { prefix, hidePrimaryCheckbox, target } = props
  prefix = prefix || ''
  const [province, township, residentialUnitType, country, latinNearestLandmark, nearestLandmark] = usePropertyReader(['province', 'township', 'residentialUnitType', 'country', 'latinNearestLandmark', 'nearestLandmark'].map(x => prefix + x))

  const { onChange: onProvinceChange } = useCoupledProps('province', ['township', 'district'])
  const { onChange: onTownshipChange } = useCoupledProps('township', ['district'])

  const isIraq = country && country.code === 'IQ'

  const showRequiredOrFreeTextField = (label, name, freeTextName, dataSource, filter = null, onChange) => {
    if (isIraq) {
      return (<FormRow label={label} name={prefix + name} inputType='lookup'
        dataSource={dataSource} onChange={onChange} clientSide isCodeList filter={filter} required target={target} />)
    } else {
      return <FormRow label={label} name={prefix + freeTextName} target={target} />
    }
  }

  return (
    <React.Fragment>
      <div className='row'>
        <div className='col-6'>
          <FormRow label='Persons.Country' name={prefix + 'country'} inputType='lookup' required disabled={!!country}
            dataSource={context.cache.getCountries} clientSide isCodeList target={target} />
          {!!country && <React.Fragment>
            <FormRow label='Persons.AddressType' name={prefix + 'addressType'} inputType='lookup' required
              dataSource={context.cache.getAddressTypes} clientSide isCodeList target={target} />
            {showRequiredOrFreeTextField('Persons.Region', 'region', 'regionFreeText', context.cache.getRegions)}
            <DualLanguageRow label={'Persons.AddressCity'} latinLabel={'Persons.AddressLatinCity'}
              name={prefix + 'city'} latinName={prefix + 'latinCity'} inputProps={{ maxLength: 100 }} required target={target} />
            <DualLanguageRow label={'Persons.AddressStreet'} latinLabel={'Persons.AddressLatinStreet'}
              name={prefix + 'street'} latinName={prefix + 'latinStreet'} inputProps={{ maxLength: 100 }} required target={target} />
            <DualLanguageRow label={'Persons.Sector'} latinLabel={'Persons.LatinSector'}
              name={prefix + 'sector'} latinName={prefix + 'latinSector'} inputProps={{ maxLength: 10 }}
              required={isIraq} target={target} />
            <FormRow label='Persons.AddressZipCode' name={prefix + 'zipCode'} inputProps={{ maxLength: 10 }}
              required={country && country.code !== 'IQ'} target={target} />
          </React.Fragment>}
        </div>
        {!!country && <div className='col-6'>
          {showRequiredOrFreeTextField('Persons.Province', 'province', 'provinceFreeText', context.cache.getProvinces, null, onProvinceChange)}
          {showRequiredOrFreeTextField('Institutions.Township', 'township', 'townshipFreeText', context.cache.getTownships, province, onTownshipChange)}
          {showRequiredOrFreeTextField('Institutions.District', 'district', 'districtFreeText', context.cache.getDistricts, township)}
          <DualLanguageRow label='Persons.ProvinceSide'
            latinLabel='Persons.LatinProvinceSide' name={prefix + 'provinceSide'} latinName={prefix + 'latinProvinceSide'} inputProps={{ maxLength: 15 }} required={isIraq} target={target} />

          <FormRow label='Persons.ResidentialUnitType' name={prefix + 'residentialUnitType'} inputType='lookup'
            onValidate={() => isIraq && !nearestLandmark && !latinNearestLandmark && !residentialUnitType ? 'Validations.Required' : false}
            dataSource={context.cache.getResidentialUnitTypes} clientSide isCodeList target={target} />
          {residentialUnitType &&
            <FormRow label='Persons.HouseNumber' disabled={residentialUnitType.code !== '1'} name={prefix + 'houseNumber'}
              inputProps={{ maxLength: 10 }} required={isIraq && !nearestLandmark && !latinNearestLandmark} target={target} />}
          {residentialUnitType &&
            <FormRow label='Persons.BuildingNumber' disabled={residentialUnitType.code !== '2'} name={prefix + 'buildingNumber'}
              inputProps={{ maxLength: 10 }} required={isIraq && !nearestLandmark && !latinNearestLandmark} target={target} />}

          <DualLanguageRow label='Persons.NearestLandmark' latinLabel='Persons.LatinNearestLandmark'
            name={prefix + 'nearestLandmark'} latinName={prefix + 'latinNearestLandmark'} inputProps={{ maxLength: 50 }}
            required={isIraq && !residentialUnitType} target={target} />
          {!hidePrimaryCheckbox && <FormRow label='Persons.PrimaryAddress' name={prefix + 'primary'} inputType='checkbox' target={target} />}
        </div>}
      </div>
      {!!country && <div className='row'>
        <div className='col-12'>
          <DualLanguageRow inputType='textarea' label='Persons.AddressDetail' latinLabel='Persons.LatinAddressDetail'
            name={prefix + 'addressDetail'} latinName={prefix + 'latinAddressDetail'} inputProps={{ maxLength: 200 }} target={target} />
        </div>
      </div>}
    </React.Fragment>
  )
})

export default {
  modalProps: {
    size: 'lg'
  },
  component: AddressDialog
}

export {
  AddressForm
}



// WEBPACK FOOTER //
// ./src/dialogs/address.js